import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import CheckoutButton from "@/components/CheckoutButton";
import { Button } from "@/components/ui/button";
import { 
  ChevronRight, 
  Check, 
  Shield, 
  Zap, 
  ArrowLeft,
  Play,
  X,
  ChevronLeft,
  Maximize2,
  PackageCheck,
  Server,
  BookOpen,
  ExternalLink,
} from "lucide-react";

const getYouTubeEmbedUrl = (videoUrl) => {
  if (!videoUrl) return "";
  try {
    const url = new URL(videoUrl);
    const hostname = url.hostname.replace(/^www\./, "");
    let videoId = "";

    if (hostname === "youtu.be") {
      videoId = url.pathname.split("/").filter(Boolean)[0] || "";
    } else if (hostname.endsWith("youtube.com")) {
      if (url.pathname === "/watch") videoId = url.searchParams.get("v") || "";
      if (url.pathname.startsWith("/shorts/") || url.pathname.startsWith("/embed/")) {
        videoId = url.pathname.split("/").filter(Boolean)[1] || "";
      }
    }

    return videoId
      ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`
      : videoUrl;
  } catch {
    return videoUrl;
  }
};

const formatCategory = (category) =>
  category
    ? category.split("-").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
    : "";

const ProductDeliveryDetails = ({ product, className = "", testIdSuffix = "" }) => {
  if (!product.whats_included?.length && !product.requirements?.length) return null;

  return (
    <div className={`grid-cols-1 gap-4 sm:grid-cols-2 ${className}`}>
      {product.whats_included?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-5">
          <h3 className="mb-4 flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground">
            <PackageCheck className="h-4 w-4 text-primary" /> What's included
          </h3>
          <ul className="space-y-3" data-testid={`product-whats-included${testIdSuffix}`}>
            {product.whats_included.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm leading-5 text-slate-300">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {product.requirements?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-5">
          <h3 className="mb-4 flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground">
            <Server className="h-4 w-4 text-primary" /> Requirements
          </h3>
          <ul className="space-y-3" data-testid={`product-requirements${testIdSuffix}`}>
            {product.requirements.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm leading-5 text-slate-300">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productRes = await api.get(`/products/${id}`);
        setProduct(productRes.data);
        setSelectedImage(0);
        setLightboxOpen(false);

        // Fetch related products
        const allProductsRes = await api.get("/products");
        const related = allProductsRes.data
          .filter(p => p.id !== id && p.category === productRes.data.category)
          .slice(0, 3);
        setRelatedProducts(related);
      } catch (e) {
        console.error("Error fetching product:", e);
      }
      setLoading(false);
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [lightboxOpen]);

  const allImages = product
    ? [product.image_url, ...(product.gallery_images || [])].filter(Boolean).slice(0, 7)
    : [];

  const showPreviousImage = () => {
    setSelectedImage((current) => (current - 1 + allImages.length) % allImages.length);
  };

  const showNextImage = () => {
    setSelectedImage((current) => (current + 1) % allImages.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 pb-16 max-w-7xl mx-auto px-6">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-800 rounded-lg w-48 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="h-96 bg-slate-800 rounded-xl" />
              <div className="space-y-4">
                <div className="h-10 bg-slate-800 rounded-lg w-3/4" />
                <div className="h-6 bg-slate-800 rounded-lg w-1/4" />
                <div className="h-32 bg-slate-800 rounded-lg" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 pb-16 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
          <Link to="/products">
            <Button variant="outline" className="rounded-lg">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Products
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/products" className="hover:text-primary transition-colors">Products</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-foreground">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-12 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Main Image */}
              <button
                type="button"
                onClick={() => allImages.length > 0 && setLightboxOpen(true)}
                className="group relative mb-4 block aspect-video w-full overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50 text-left focus:outline-none focus:ring-2 focus:ring-primary/60"
                aria-label={`Open ${product.name} image full screen`}
              >
                <img
                  src={allImages[selectedImage] || "/aerocore-logo-v2.svg"}
                  alt={product.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  data-testid="product-main-image"
                />
                {allImages.length > 0 && (
                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-950/80 px-3 py-2 text-xs font-semibold text-white opacity-100 backdrop-blur transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                    <Maximize2 className="h-4 w-4 text-primary" /> View full screen
                  </span>
                )}
                {product.is_featured && (
                  <div className="absolute top-3 right-3 px-3 py-1.5 bg-primary/90 rounded-full text-white text-[10px] font-bold uppercase tracking-wider">
                    FEATURED
                  </div>
                )}
              </button>

              {/* Thumbnail Gallery */}
              {allImages.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Gallery</h2>
                    <span className="text-xs text-slate-600">{selectedImage + 1} / {allImages.length}</span>
                  </div>
                  <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-2 no-scrollbar sm:grid sm:grid-cols-4 sm:overflow-visible">
                  {allImages.map((img, index) => (
                    <button
                      key={`${img}-${index}`}
                      type="button"
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-video w-28 flex-shrink-0 snap-start overflow-hidden rounded-lg border sm:w-auto ${
                        selectedImage === index
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-slate-800 opacity-70 hover:border-slate-600 hover:opacity-100"
                      } transition-all`}
                      aria-label={`Show image ${index + 1}`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                  </div>
                </div>
              )}

              {/* Video Embed */}
              {product.video_url && (
                <div className="mt-6">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Demo Video
                  </h3>
                  <div className="aspect-video bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
                    <iframe
                      src={getYouTubeEmbedUrl(product.video_url)}
                      title="Product Demo"
                      className="w-full h-full"
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
              {!product.video_url && (
                <div className="mt-6 flex min-h-40 items-center justify-center rounded-xl border border-dashed border-slate-800 bg-slate-950/35 px-6 text-center">
                  <div>
                    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-primary/20 bg-primary/5">
                      <Play className="h-5 w-5 text-primary/70" />
                    </div>
                    <p className="text-sm font-semibold text-slate-300">Product demo</p>
                    <p className="mt-1 text-xs text-slate-600">A video walkthrough will appear here when available.</p>
                  </div>
                </div>
              )}

              <ProductDeliveryDetails
                product={product}
                className="mt-6 hidden lg:grid"
                testIdSuffix="-desktop"
              />
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Category */}
              {product.category && (
                <span className="text-xs uppercase tracking-widest text-primary mb-3 block">
                  {formatCategory(product.category)}
                </span>
              )}

              {/* Title */}
              <h1 
                className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-4"
                data-testid="product-title"
              >
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-center gap-4 mb-6">
                <span className="price-tag text-3xl" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                <span className="text-sm text-muted-foreground">USD</span>
              </div>

              {/* Short Description */}
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {product.short_description}
              </p>

              {/* CTA Buttons */}
              <CheckoutButton
                product={product}
                testId="add-to-cart-btn"
                className="mb-3 w-full rounded-lg border-l-4 border-accent bg-primary py-6 font-bold uppercase tracking-wider text-primary-foreground hover:bg-primary/90 glow-blue-hover btn-mechanical"
              />
              <Button
                asChild
                variant="outline"
                className="mb-3 h-12 w-full rounded-lg border-primary/35 bg-primary/5 font-bold uppercase tracking-wider text-primary hover:border-accent hover:bg-primary/10 hover:text-accent"
              >
                <a
                  href="https://docs.aerocorescripts.com/getting-started/introduction"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="product-documentation-link"
                >
                  <BookOpen className="h-4 w-4" />
                  Documentation
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
              <p className="mb-8 text-xs leading-5 text-slate-500">
                FiveM and Discord are required before an item can be added. Use Login above or follow the prompts.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="w-4 h-4 text-primary" />
                  <span>Tebex Secure</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>Delivery through Tebex</span>
                </div>
              </div>

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                    Features
                  </h3>
                  <ul className="space-y-3">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-primary/10 border border-primary/30 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Compatibility */}
              {product.compatibility?.length > 0 && (
                <div className="mb-8 border-t border-slate-800/60 pt-7">
                  <h3 className="mb-4 text-sm uppercase tracking-widest text-muted-foreground">
                    Compatibility
                  </h3>
                  <div className="flex flex-wrap gap-2" data-testid="product-compatibility">
                    {product.compatibility.map((item) => (
                      <span
                        key={item}
                        className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs font-bold uppercase tracking-wider text-primary"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <ProductDeliveryDetails product={product} className="mb-8 grid lg:hidden" />

            </motion.div>

            {/* Full Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-slate-800/50 pt-10 lg:col-span-2"
            >
              <h2 className="mb-5 text-sm uppercase tracking-widest text-muted-foreground">
                Full Description
              </h2>
              <div className="prose prose-invert prose-sm max-w-none">
                <p className="whitespace-pre-wrap text-muted-foreground leading-relaxed">
                  {product.full_description}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightboxOpen && allImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxOpen(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 p-4 backdrop-blur-md sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-label={`${product.name} image gallery`}
          >
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-900/90 text-white transition-colors hover:border-primary hover:text-primary sm:right-7 sm:top-7"
              aria-label="Close gallery"
            >
              <X className="h-5 w-5" />
            </button>

            {allImages.length > 1 && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  showPreviousImage();
                }}
                className="absolute left-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-900/90 text-white transition-colors hover:border-primary hover:text-primary sm:left-7"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            <motion.div
              key={selectedImage}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={(event) => event.stopPropagation()}
              className="flex max-h-full w-full max-w-6xl flex-col items-center"
            >
              <img
                src={allImages[selectedImage]}
                alt={`${product.name} full-screen view ${selectedImage + 1}`}
                className="max-h-[78vh] max-w-full rounded-xl border border-slate-800 object-contain shadow-2xl"
              />
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                {product.name} · {selectedImage + 1} / {allImages.length}
              </p>
            </motion.div>

            {allImages.length > 1 && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  showNextImage();
                }}
                className="absolute right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-900/90 text-white transition-colors hover:border-primary hover:text-primary sm:right-7"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 border-t border-slate-800/50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl font-bold tracking-tight uppercase mb-8">
              Related Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
